import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthModal from '../auth/AuthModal';
import { useAuth } from '../../hooks/useAuth';
import VisitorLogsModal from '../admin/VisitorLogsModal';

interface NavigationProps {
  onContactClick?: () => void;
}

export default function Navigation({ onContactClick }: NavigationProps) {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisitorLogsOpen, setIsVisitorLogsOpen] = useState(false);
  const [showSignOutToast, setShowSignOutToast] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContactClick) {
      onContactClick();
    }
  };

  const handleVisitorLogsClick = () => {
    setIsVisitorLogsOpen(true);
  };

  const handleSignOut = async () => {
    await signOut();
    setShowSignOutToast(true);
    setTimeout(() => setShowSignOutToast(false), 2500);
    navigate('/');
  };

  return (
    <>
      <nav className="bg-white shadow-lg fixed top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-book-open-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                Math&PhysicsVault by Mr. Hidayat Khan
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === '/' 
                    ? 'text-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Home
              </Link>
              <Link
                to="/resources"
                className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === '/resources' 
                    ? 'text-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Resources
              </Link>
              <Link
                to="/about"
                className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === '/about' 
                    ? 'text-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                About
              </Link>
              <a
                href="#contact"
                onClick={handleContactClick}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Me
              </a>
              
              {isAdmin() && (
                <button
                  onClick={handleVisitorLogsClick}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  title="Visitor Logs"
                  aria-label="Open Visitor Logs"
                >
                  <i className="ri-file-list-3-line text-gray-700 text-xl"></i>
                </button>
              )}
              
              {user ? (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Welcome, {user.email}</span>
                  <button
                    onClick={handleSignOut}
                    className="text-gray-700 hover:text-red-500 font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors whitespace-nowrap"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600 transition-colors"
              >
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <Link
                  to="/"
                  className={`block px-3 py-2 rounded-md font-medium transition-colors ${
                    location.pathname === '/' 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/resources"
                  className={`block px-3 py-2 rounded-md font-medium transition-colors ${
                    location.pathname === '/resources' 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Resources
                </Link>
                <Link
                  to="/about"
                  className={`block px-3 py-2 rounded-md font-medium transition-colors ${
                    location.pathname === '/about' 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About
                </Link>
                <a
                  href="#contact"
                  onClick={(e) => {
                    handleContactClick(e);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 rounded-md font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Contact Me
                </a>
                
                {isAdmin() && (
                  <button
                    onClick={() => {
                      setIsVisitorLogsOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-file-list-3-line mr-2"></i>
                    Visitor Logs
                  </button>
                )}
                
                {user ? (
                  <div className="border-t border-gray-200 pt-2">
                    <div className="px-3 py-2 text-sm text-gray-600">Welcome, {user.email}</div>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 rounded-md font-medium text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="border-t border-gray-200 pt-2">
                    <button
                      onClick={() => {
                        setIsAuthModalOpen(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 rounded-md font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {isAuthModalOpen && (
        <AuthModal 
          onClose={() => setIsAuthModalOpen(false)} 
        />
      )}

      <VisitorLogsModal 
        isOpen={isVisitorLogsOpen}
        onClose={() => setIsVisitorLogsOpen(false)} 
      />

      {/* Sign Out Toast */}
      {showSignOutToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-xl flex items-center space-x-2 animate-fade-in">
          <i className="ri-checkbox-circle-line text-green-400 text-lg w-5 h-5 flex items-center justify-center"></i>
          <span className="text-sm font-medium">Signed out successfully</span>
        </div>
      )}
    </>
  );
}
